import { useEffect, useRef, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import styled, { keyframes } from "styled-components"

import SupportTicketService from "~services/SupportTicketService"
import UserService from "~services/UserService"
import PageContent from "~components/PageContent"
import PageHeader from "~components/PageHeader"
import { Card } from "~components/Card"
import TablePaginator from "~components/TablePaginator"
import {
  Table,
  TableContainer,
  Th,
  Tr,
  Td,
  NoTableData,
  TableTopGroup,
} from "~styled/table"
import { TablePaginatorShape } from "~shapes/api-table"
import {
  TicketShape,
  TicketStatus,
  TicketCategory,
  TicketPriority,
  TicketListResponse,
  TICKET_STATUS_LABELS,
  TICKET_CATEGORY_LABELS,
  ALL_TICKET_STATUSES,
  ALL_TICKET_CATEGORIES,
} from "~shapes/support-ticket"
import PATHS, { getFullPath } from "~constants/paths"
import colors from "~constants/colors"

type SortOption = "created_asc" | "created_desc" | "updated_desc"
type EmailSearchStatus = "idle" | "searching" | "found" | "not_found"

const SORT_CONFIG: Record<
  SortOption,
  { label: string; order_by: string; order_dir: "asc" | "desc" }
> = {
  created_asc: {
    label: "Oldest first",
    order_by: "created_at",
    order_dir: "asc",
  },
  created_desc: {
    label: "Newest first",
    order_by: "created_at",
    order_dir: "desc",
  },
  updated_desc: {
    label: "Last updated first",
    order_by: "updated_at",
    order_dir: "desc",
  },
}

const POLL_INTERVAL_MS = 2 * 60 * 1000

const STATUS_CONFIG: Record<
  TicketStatus,
  { background: string; color: string }
> = {
  NEW: { background: "#EEF2FF", color: "#5E81F4" },
  AUTOMATION_QUEUE: { background: "#F3E8FF", color: "#7C3AED" },
  OPEN: { background: "#ECFDF5", color: "#16A34A" },
  WAITING_FOR_USER: { background: "#FFF7ED", color: "#EA580C" },
  RESOLVED: { background: "#F3F4F6", color: "#6B7280" },
  CLOSED: { background: "#FFF1F2", color: "#F54B5E" },
}

const PRIORITY_BADGE_CONFIG: Record<
  TicketPriority,
  { background: string; color: string }
> = {
  HIGH: { background: "#FEE2E2", color: "#DC2626" },
  NORMAL: { background: "#F3F4F6", color: "#4B5563" },
  LOW: { background: "#F9FAFB", color: "#9CA3AF" },
}

const formatRelativeTime = (date: Date): string => {
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffMins < 1) return "Just now"
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

const toPaginatorShape = (res: TicketListResponse): TablePaginatorShape => ({
  size: res.page_size,
  number: res.page - 1,
  totalPages: Math.ceil(res.total / res.page_size) || 1,
  totalElements: res.total,
})

const CONTROL_STYLE: React.CSSProperties = {
  height: 40,
  padding: "0 12px",
  borderRadius: 6,
  border: "1px solid #ddd",
  fontSize: 14,
}

const SupportTickets = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const [tickets, setTickets] = useState<TicketShape[]>([])
  const [paginator, setPaginator] = useState<TablePaginatorShape | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [statusFilter, setStatusFilter] = useState<TicketStatus>(
    (searchParams.get("status") as TicketStatus) ?? "NEW",
  )
  const [sortOption, setSortOption] = useState<SortOption>(
    (searchParams.get("sort") as SortOption) ?? "created_asc",
  )
  const [emailInput, setEmailInput] = useState(searchParams.get("email") ?? "")
  const [resolvedUserId, setResolvedUserId] = useState<string | null>(
    searchParams.get("userId") ?? null,
  )
  const [emailStatus, setEmailStatus] = useState<EmailSearchStatus>(
    searchParams.get("userId") ? "found" : "idle",
  )
  const [categoryFilter, setCategoryFilter] = useState<TicketCategory | "">(
    (searchParams.get("category") as TicketCategory) ?? "",
  )
  const [unreadOnly, setUnreadOnly] = useState<boolean>(
    searchParams.get("unread") === "1",
  )
  const [newTicketsCount, setNewTicketsCount] = useState(0)

  // Refs to avoid stale closures in the poll interval
  const statusFilterRef = useRef(statusFilter)
  const sortOptionRef = useRef(sortOption)
  const resolvedUserIdRef = useRef(resolvedUserId)
  const categoryFilterRef = useRef(categoryFilter)
  const lastKnownTotalRef = useRef<number | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    statusFilterRef.current = statusFilter
  }, [statusFilter])
  useEffect(() => {
    sortOptionRef.current = sortOption
  }, [sortOption])
  useEffect(() => {
    resolvedUserIdRef.current = resolvedUserId
  }, [resolvedUserId])
  useEffect(() => {
    categoryFilterRef.current = categoryFilter
  }, [categoryFilter])

  const updateSearchParams = (updates: Record<string, string | null>) => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev)
        for (const [k, v] of Object.entries(updates)) {
          if (v === null || v === "") next.delete(k)
          else next.set(k, v)
        }
        return next
      },
      { replace: true },
    )
  }

  // Start polling on mount, clear on unmount
  useEffect(() => {
    pollRef.current = setInterval(pollForNewTickets, POLL_INTERVAL_MS)
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [])

  const pollForNewTickets = async () => {
    try {
      const userId = resolvedUserIdRef.current
      const { order_by, order_dir } = SORT_CONFIG[sortOptionRef.current]
      const category = categoryFilterRef.current
      const res = await SupportTicketService.getTickets({
        page_size: 1,
        order_by,
        order_dir,
        // Omit status when filtering by user so we poll across all statuses
        ...(userId ? {} : { status: statusFilterRef.current }),
        ...(userId ? { user_id: userId } : {}),
        ...(category ? { category } : {}),
      })
      if (
        lastKnownTotalRef.current !== null &&
        res.total > lastKnownTotalRef.current
      ) {
        setNewTicketsCount(res.total - lastKnownTotalRef.current)
      }
    } catch {
      // ignore poll errors silently
    }
  }

  const fetchTickets = async (params?: {
    status?: TicketStatus
    sort?: SortOption
    user_id?: string | null
    page?: number
    category?: TicketCategory | ""
  }) => {
    setIsLoading(true)
    try {
      const userId =
        "user_id" in (params ?? {}) ? params!.user_id : resolvedUserId
      const { order_by, order_dir } = SORT_CONFIG[params?.sort ?? sortOption]
      const activeCategory =
        "category" in (params ?? {}) ? params!.category : categoryFilter
      const res = await SupportTicketService.getTickets({
        page_size: 20,
        order_by,
        order_dir,
        // Omit status when filtering by user so results span all statuses
        ...(userId ? {} : { status: params?.status ?? statusFilter }),
        page: params?.page,
        ...(userId ? { user_id: userId } : { user_id: undefined }),
        ...(activeCategory ? { category: activeCategory } : {}),
      })
      setTickets(res.items)
      setPaginator(toPaginatorShape(res))
      lastKnownTotalRef.current = res.total
      setNewTicketsCount(0)
    } catch {
      setTickets([])
      setPaginator(null)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    const page = parseInt(searchParams.get("page") ?? "1", 10)
    fetchTickets({ page })
  }, [])

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value as TicketStatus
    setStatusFilter(val)
    updateSearchParams({ status: val, page: null })
    fetchTickets({ status: val, page: 1 })
  }

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value as SortOption
    setSortOption(val)
    updateSearchParams({ sort: val, page: null })
    fetchTickets({ sort: val, page: 1 })
  }

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value as TicketCategory | ""
    setCategoryFilter(val)
    updateSearchParams({ category: val || null, page: null })
    fetchTickets({ category: val, page: 1 })
  }

  const handleUnreadOnlyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked
    setUnreadOnly(checked)
    updateSearchParams({ unread: checked ? "1" : null })
  }

  const handlePageChange = (pageIndex: number) => {
    const page = pageIndex + 1
    updateSearchParams({ page: String(page) })
    fetchTickets({ page })
  }

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    setEmailInput(val)

    if (debounceRef.current) clearTimeout(debounceRef.current)

    if (!val.trim()) {
      setEmailStatus("idle")
      setResolvedUserId(null)
      updateSearchParams({ email: null, userId: null, page: null })
      fetchTickets({ user_id: null })
      return
    }

    updateSearchParams({ email: val, userId: null, page: null })
    setEmailStatus("searching")
    debounceRef.current = setTimeout(async () => {
      try {
        const user = await UserService.getUserByEmail(val.trim())
        const userId = user?.id ?? user?._embedded?.users?.[0]?.id
        if (userId) {
          setResolvedUserId(userId)
          setEmailStatus("found")
          updateSearchParams({ userId })
          fetchTickets({ user_id: userId })
        } else {
          setResolvedUserId(null)
          setEmailStatus("not_found")
          fetchTickets({ user_id: null })
        }
      } catch {
        setResolvedUserId(null)
        setEmailStatus("not_found")
        fetchTickets({ user_id: null })
      }
    }, 600)
  }

  const visibleTickets = unreadOnly
    ? tickets.filter(t => t.has_unread_activity)
    : tickets

  return (
    <PageContent>
      <PageHeader title="Support Tickets" />
      <Card>
        {newTicketsCount > 0 && (
          <NewTicketsBanner>
            <BannerDot />
            <BannerText>
              {newTicketsCount} new ticket{newTicketsCount > 1 ? "s" : ""} since
              last refresh
            </BannerText>
            <BannerRefreshButton onClick={() => fetchTickets()}>
              Refresh now
            </BannerRefreshButton>
            <BannerDismiss onClick={() => setNewTicketsCount(0)}>
              ✕
            </BannerDismiss>
          </NewTicketsBanner>
        )}

        <TableTopGroup style={{ marginBottom: 40 }}>
          <EmailInputWrapper>
            <input
              type="email"
              placeholder="Search by user email…"
              value={emailInput}
              onChange={handleEmailChange}
              style={{ ...CONTROL_STYLE, width: 240, cursor: "text" }}
            />
            {emailStatus === "searching" && <EmailHint>Searching…</EmailHint>}
            {emailStatus === "found" && (
              <EmailHint>User found · {resolvedUserId}</EmailHint>
            )}
            {emailStatus === "not_found" && (
              <EmailHint $error>No user found for this email</EmailHint>
            )}
          </EmailInputWrapper>

          <div style={{ display: "flex", gap: 8 }}>
            <select
              value={statusFilter}
              onChange={handleStatusChange}
              disabled={!!resolvedUserId}
              title={
                resolvedUserId
                  ? "Showing all statuses while filtering by email"
                  : undefined
              }
              style={{
                ...CONTROL_STYLE,
                cursor: resolvedUserId ? "not-allowed" : "pointer",
                opacity: resolvedUserId ? 0.45 : 1,
              }}
            >
              {ALL_TICKET_STATUSES.map(s => (
                <option key={s} value={s}>
                  {TICKET_STATUS_LABELS[s]}
                </option>
              ))}
            </select>
            <select
              value={categoryFilter}
              onChange={handleCategoryChange}
              style={{ ...CONTROL_STYLE, cursor: "pointer" }}
            >
              <option value="">All categories</option>
              {ALL_TICKET_CATEGORIES.map(c => (
                <option key={c} value={c}>
                  {TICKET_CATEGORY_LABELS[c]}
                </option>
              ))}
            </select>
            <select
              value={sortOption}
              onChange={handleSortChange}
              style={{ ...CONTROL_STYLE, cursor: "pointer" }}
            >
              {(Object.keys(SORT_CONFIG) as SortOption[]).map(key => (
                <option key={key} value={key}>
                  {SORT_CONFIG[key].label}
                </option>
              ))}
            </select>
            <UnreadToggleLabel>
              <input
                type="checkbox"
                checked={unreadOnly}
                onChange={handleUnreadOnlyChange}
              />
              Unread only
            </UnreadToggleLabel>
          </div>
        </TableTopGroup>

        <TableContainer>
          <Table>
            <colgroup>
              <col style={{ width: "24%" }} />
              <col style={{ width: "16%" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "13%" }} />
              <col style={{ width: "15%" }} />
            </colgroup>
            <thead>
              <tr>
                <Th style={{ textAlign: "left" }}>Ticket</Th>
                <Th style={{ textAlign: "left" }}>User</Th>
                <Th style={{ textAlign: "left" }}>Status</Th>
                <Th style={{ textAlign: "left" }}>Priority</Th>
                <Th style={{ textAlign: "center" }}>Msgs</Th>
                <Th style={{ textAlign: "left" }}>Created</Th>
                <Th style={{ textAlign: "left" }}>Last Update</Th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <Td colSpan={7}>
                    <div>Loading…</div>
                  </Td>
                </tr>
              ) : visibleTickets.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <NoTableData>No tickets found.</NoTableData>
                  </td>
                </tr>
              ) : (
                visibleTickets.map(ticket => (
                  <TicketRow
                    key={ticket.id}
                    isClickable
                    $unread={!!ticket.has_unread_activity}
                    onClick={() => {
                      const qs = new URLSearchParams()
                      if (statusFilter !== "NEW") qs.set("status", statusFilter)
                      if (sortOption !== "created_asc")
                        qs.set("sort", sortOption)
                      if (paginator && paginator.number + 1 > 1)
                        qs.set("page", String(paginator.number + 1))
                      if (resolvedUserId) qs.set("userId", resolvedUserId)
                      if (emailInput) qs.set("email", emailInput)
                      if (categoryFilter) qs.set("category", categoryFilter)
                      const backTo = `${getFullPath(PATHS.supportTickets)}${qs.toString() ? `?${qs}` : ""}`
                      sessionStorage.setItem("supportTicketsBack", backTo)
                      navigate(
                        `${getFullPath(PATHS.supportTickets)}/${ticket.id}`,
                      )
                    }}
                  >
                    <Td>
                      <TitleCell>
                        {ticket.has_unread_activity && (
                          <UnreadDot aria-label="Unread activity" />
                        )}
                        <div style={{ minWidth: 0 }}>
                          <TitleText $unread={!!ticket.has_unread_activity}>
                            {TICKET_CATEGORY_LABELS[
                              ticket.title as TicketCategory
                            ] ?? ticket.title}
                          </TitleText>
                          {ticket.category && (
                            <CategorySubtext>
                              {TICKET_CATEGORY_LABELS[
                                ticket.category as TicketCategory
                              ] ?? ticket.category}
                            </CategorySubtext>
                          )}
                        </div>
                      </TitleCell>
                    </Td>
                    <Td>
                      <UserText $unread={!!ticket.has_unread_activity}>
                        {ticket.user_email ?? ticket.user_id}
                      </UserText>
                    </Td>
                    <Td>
                      <StatusBadge $status={ticket.status}>
                        {TICKET_STATUS_LABELS[ticket.status]}
                      </StatusBadge>
                    </Td>
                    <Td>
                      <PriorityBadge $priority={ticket.priority}>
                        {ticket.priority.charAt(0) +
                          ticket.priority.slice(1).toLowerCase()}
                      </PriorityBadge>
                    </Td>
                    <Td>
                      <div style={{ textAlign: "center" }}>
                        <MsgCount
                          $unread={!!ticket.has_unread_activity}
                          $hasMessages={(ticket.messages_count ?? 0) > 0}
                        >
                          {ticket.messages_count ?? 0}
                        </MsgCount>
                      </div>
                    </Td>
                    <Td>
                      <DateText $unread={!!ticket.has_unread_activity}>
                        {new Date(ticket.created_at).toLocaleDateString(
                          undefined,
                          { month: "short", day: "numeric", year: "numeric" },
                        )}
                      </DateText>
                    </Td>
                    <Td>
                      <DateText $unread={!!ticket.has_unread_activity}>
                        {formatRelativeTime(new Date(ticket.updated_at))}
                      </DateText>
                    </Td>
                  </TicketRow>
                ))
              )}
            </tbody>
          </Table>
        </TableContainer>

        {paginator && paginator.totalPages > 1 && (
          <TablePaginator page={paginator} onPageChange={handlePageChange} />
        )}
      </Card>
    </PageContent>
  )
}

export default SupportTickets

// Styled components

const TicketRow = styled(Tr)<{ $unread: boolean }>`
  background-color: ${({ $unread }) => ($unread ? "#EEF2FF" : "#fff")};
  transition:
    background-color 0.15s ease,
    box-shadow 0.15s ease;

  &:hover {
    background-color: ${({ $unread }) => ($unread ? "#DDE5FF" : "#F8F9FB")};
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  }
`

const StatusBadge = styled.span<{ $status: TicketStatus }>`
  background: ${({ $status }) => STATUS_CONFIG[$status].background};
  color: ${({ $status }) => STATUS_CONFIG[$status].color};
  border-radius: 20px;
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  line-height: 1;
  padding: 5px 10px;
  white-space: nowrap;
`

const PriorityBadge = styled.span<{ $priority: TicketPriority }>`
  background: ${({ $priority }) => PRIORITY_BADGE_CONFIG[$priority].background};
  color: ${({ $priority }) => PRIORITY_BADGE_CONFIG[$priority].color};
  border-radius: 20px;
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  line-height: 1;
  padding: 5px 10px;
  white-space: nowrap;
`

const MsgCount = styled.span<{ $unread: boolean; $hasMessages: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 22px;
  padding: 0 6px;
  border-radius: 11px;
  font-size: 12px;
  font-weight: ${({ $unread }) => ($unread ? 700 : 500)};
  background: ${({ $unread, $hasMessages }) =>
    $unread ? "#5E81F4" : $hasMessages ? "#E5E7EB" : "transparent"};
  color: ${({ $unread, $hasMessages }) =>
    $unread ? "#fff" : $hasMessages ? "#374151" : "#9CA3AF"};
`

const UserText = styled.div<{ $unread: boolean }>`
  color: ${({ $unread }) => ($unread ? "#1C1D21" : "#6B7280")};
  font-size: 13px;
  font-weight: ${({ $unread }) => ($unread ? 600 : 400)};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const DateText = styled.div<{ $unread: boolean }>`
  color: ${({ $unread }) => ($unread ? "#374151" : "#9CA3AF")};
  font-size: 12px;
  font-weight: ${({ $unread }) => ($unread ? 500 : 400)};
  white-space: nowrap;
`

const CategorySubtext = styled.div`
  font-size: 11px;
  color: #9ca3af;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const EmailInputWrapper = styled.div`
  position: relative;
`

const TitleCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`

const TitleText = styled.span<{ $unread: boolean }>`
  font-weight: ${({ $unread }) => ($unread ? 700 : 500)};
  color: ${({ $unread }) => ($unread ? "#1C1D21" : "#374151")};
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
`

const UnreadDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${colors.blue1};
  flex-shrink: 0;
  box-shadow: 0 0 0 2px rgba(94, 129, 244, 0.18);
`

const UnreadToggleLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #4b5563;
  cursor: pointer;
  user-select: none;
  padding: 0 10px;
  height: 40px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: #fff;
`

const EmailHint = styled.span<{ $error?: boolean }>`
  font-size: 11px;
  color: ${({ $error }) => ($error ? "#F54B5E" : "#16A34A")};
  position: absolute;
  bottom: -18px;
  left: 2px;
  white-space: nowrap;
`

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`

const NewTicketsBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: #eef2ff;
  border: 1px solid #c7d2fe;
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 20px;
  font-size: 13px;
`

const BannerDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${colors.blue2};
  flex-shrink: 0;
  animation: ${pulse} 1.8s ease-in-out infinite;
`

const BannerText = styled.span`
  color: ${colors.blue1};
  font-weight: 500;
  flex: 1;
`

const BannerRefreshButton = styled.button`
  background: ${colors.blue1};
  color: white;
  border: none;
  border-radius: 6px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    opacity: 0.88;
  }
`

const BannerDismiss = styled.button`
  background: none;
  border: none;
  color: ${colors.blue2};
  cursor: pointer;
  font-size: 12px;
  padding: 0 2px;
  line-height: 1;
  opacity: 0.6;
  &:hover {
    opacity: 1;
  }
`
